import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ModalBasic } from "../../../shared/components/ModalBasic";
import { FileList } from "./Components/FileList";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { fetchAllStorageObjects } from "../../../app/state/storageObjectSlice/actions/actions";
import { useStorageSelectors } from "../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { filteredItemsByFileType } from "./helpers";
import { TModalsIds } from "../../../app/state/modalSlice/types";
import { useModalSelectors } from "../../../app/state/modalSlice/hook/use-modal-selectors";
import styles from "./styles.module.css";

interface Props {
  extensions: string[];
  onClose: (id: TModalsIds) => void;
  handleClick: (hash: string, fileName: string) => void;
  multiple: boolean;
  className?: string;
}

export const AttachedFileModal = ({
  extensions,
  onClose,
  handleClick,
  multiple,
  className,
}: Props) => {
  const dispatch = useAppDispatch();
  const attachedFileModal = useModalSelectors("attached_file_modal");
  const { menuItems } = useStorageSelectors("userfiles");
  const { t } = useTranslation();

  const [selectedFiles, setSelectedFiles] = useState<
    Array<{ hash: string; name: string }>
  >([]);

  useEffect(() => {
    if (attachedFileModal.isOpen) {
      dispatch(fetchAllStorageObjects({ service: "userfiles" }));
    }
  }, [attachedFileModal.isOpen, dispatch]);

  const handleSingleFileClick = (hash: string, fileName: string) => {
    handleClick(hash, fileName);
    onClose("attached_file_modal");
  };

  const handleOk = () => {
    selectedFiles.forEach((file) => handleClick(file.hash, file.name));
    onClose("attached_file_modal");
  };

  const handleCancel = () => {
    onClose("attached_file_modal");
  };

  const documentFiles = useMemo(
    () => filteredItemsByFileType(extensions, menuItems),
    [menuItems, extensions],
  );

  return (
    <ModalBasic
      key={attachedFileModal.subtype}
      open={attachedFileModal.isOpen}
      closeModal={() => onClose("attached_file_modal")}
      onClose={() => onClose("attached_file_modal")}
      hasHeader
      title={t("common.choose_the_file")}
      onOk={multiple ? handleOk : undefined}
      buttonOkText={t("common.confirm_button")}
      onCancel={multiple ? handleCancel : undefined}
      hasCloseButton
      hasFooter={multiple}
    >
      <div className={styles["modal-body"]}>
        <FileList
          files={documentFiles}
          multiple={multiple}
          onFileClick={handleSingleFileClick}
          onSelectionChange={multiple ? setSelectedFiles : undefined}
        />
      </div>
    </ModalBasic>
  );
};
